import COEFFICIENTS from './coefficients.json';
// Single source of truth for the µmol/L<->mg/dL factor (88.4). Confirmed export
// at CrClCalculatorModal/calculateCrCl.js:1, which has zero imports (no cycle risk).
import { UMOL_PER_L_TO_MG_PER_DL } from 'components/Modals/CrClCalculatorModal/calculateCrCl';

export const MG_DL_TO_MMOL_L = 0.02586; // cholesterol mg/dL -> mmol/L (PREVENT is mmol/L-native)

// CKD-EPI 2021 race-free eGFR (mL/min/1.73m^2). scrUnit defaults to 'umol/L' (Care&/Dynacare SI).
// Constants verified against NKF + NIDDK: 142, kappa 0.7(F)/0.9(M), alpha -0.241(F)/-0.302(M),
// exponent -1.200 (NOT the older -1.209), female x1.012, x0.9938^age.
export function calculateEgfrCkdEpi2021({ serumCreatinine, scrUnit = 'umol/L', ageYears, gender }) {
  let scr = parseFloat(serumCreatinine);
  const age = parseFloat(ageYears);
  if (!isFinite(scr) || !isFinite(age) || scr <= 0 || (gender !== 'Female' && gender !== 'Male'))
    return null;
  if (scrUnit === 'umol/L') scr = scr / UMOL_PER_L_TO_MG_PER_DL; // -> mg/dL
  const female = gender === 'Female';
  const kappa = female ? 0.7 : 0.9;
  const alpha = female ? -0.241 : -0.302;
  const r = scr / kappa;
  return (
    142 *
    Math.pow(Math.min(r, 1), alpha) *
    Math.pow(Math.max(r, 1), -1.2) *
    Math.pow(0.9938, age) *
    (female ? 1.012 : 1)
  );
}

// AHA PREVENT logistic risk. Centering/spline math verified against bcjaeger/PooledCohort
// R/prevent.R; coefficients verified against data-raw/coefs_prevent.xlsx (base_10) and
// cross-validated by reproducing the preventr package's full 5-outcome reference fixture.
// cholesterolUnit defaults to 'mmol/L' (lab data is SI; no conversion on the common path).
export function calculatePrevent({
  gender,
  ageYears,
  totalCholesterol,
  hdl,
  cholesterolUnit = 'mmol/L',
  systolicBP,
  eGFR,
  bmi,
  bpMedication,
  diabetes,
  smoker,
  statin,
  model = 'base',
  horizon = '10',
  outcome = 'ascvd',
}) {
  const sexKey = gender === 'Female' ? 'female' : gender === 'Male' ? 'male' : null;
  const c =
    COEFFICIENTS &&
    COEFFICIENTS[model] &&
    COEFFICIENTS[model][horizon] &&
    COEFFICIENTS[model][horizon][outcome] &&
    COEFFICIENTS[model][horizon][outcome][sexKey];
  if (!c) return null;

  const age = parseFloat(ageYears);
  let tc = parseFloat(totalCholesterol);
  let hdlc = parseFloat(hdl);
  const sbp = parseFloat(systolicBP);
  const egfr = parseFloat(eGFR);
  const bmiVal = bmi != null && bmi !== '' ? parseFloat(bmi) : 30; // PREVENT default; beta=0 for 10-yr ASCVD
  if ([age, tc, hdlc, sbp, egfr].some((v) => !isFinite(v))) return null;

  if (cholesterolUnit === 'mg/dL') {
    tc *= MG_DL_TO_MMOL_L;
    hdlc *= MG_DL_TO_MMOL_L;
  }
  // tc, hdlc are now mmol/L.

  const dm = diabetes ? 1 : 0;
  const smk = smoker ? 1 : 0;
  const bpTx = bpMedication ? 1 : 0;
  const stat = statin ? 1 : 0;
  const cAge = (age - 55) / 10;
  const nonHdl = tc - hdlc - 3.5;
  const cHdl = (hdlc - 1.3) / 0.3;
  const sbpGte = (Math.max(sbp, 110) - 130) / 20;
  const bmiGte = (Math.max(bmiVal, 30) - 30) / 5;
  const egfrLt = (Math.min(egfr, 60) - 60) / -15; // NEGATIVE divisor (verified vs R/prevent.R)

  const terms = {
    age: cAge,
    age_squared: cAge * cAge,
    non_hdl_c: nonHdl,
    hdl_c: cHdl,
    sbp_lt110: (Math.min(sbp, 110) - 110) / 20,
    sbp_gte110: sbpGte,
    diabetes: dm,
    current_smoking: smk,
    bmi_lt30: (Math.min(bmiVal, 30) - 25) / 5,
    bmi_gte30: bmiGte,
    egfr_lt60: egfrLt,
    egfr_gte60: (Math.max(egfr, 60) - 90) / -15,
    bp_tx: bpTx,
    statin: stat,
    bp_tx_sbp_gte110: bpTx * sbpGte,
    statin_non_hdl_c: stat * nonHdl,
    age_non_hdl_c: cAge * nonHdl,
    age_hdl_c: cAge * cHdl,
    age_sbp_gte110: cAge * sbpGte,
    age_dm: cAge * dm,
    age_smoking: cAge * smk,
    age_bmi_gte30: cAge * bmiGte,
    age_egfr_lt60: cAge * egfrLt,
  };

  let lp = c.const;
  for (const k of Object.keys(terms)) lp += (c[k] || 0) * terms[k];
  return (1 / (1 + Math.exp(-lp))) * 100; // percent
}

// ACC/AHA ASCVD risk strata.
export function getPreventRiskCategory(pct) {
  if (pct == null || isNaN(pct)) return null;
  if (pct < 5) return 'Low';
  if (pct < 7.5) return 'Borderline';
  if (pct < 20) return 'Intermediate';
  return 'High';
}

// PREVENT validity ranges — for the modal-layer guard (warn-only, mirrors FIB-4).
export const PREVENT_RANGES = {
  age: { min: 30, max: 79 },
  systolicBP: { min: 90, max: 180 },
  eGFR: { min: 15, max: 140 },
};
